/** Shared ERP modal form layout — Arabic RTL field labels. */
import React from 'react';

export const ERP_MODAL_FORM_CLASS = 'qawaem-erp-form';

/** Modal tab rules: ≤8 fields → no tabs | 9–17 → two tabs | ≥18 → two or three tabs. */
export const ERP_MODAL_TAB_RULES = {
  NO_TABS_MAX: 8,
  TWO_TABS_MAX: 17,
  THREE_TABS_MIN_FIELDS: 27,
};

export function countModalFormFields(formSections) {
  return (formSections || []).reduce((n, section) => n + (section.fields?.length || 0), 0);
}

/**
 * Normalize modal sections to ERP tab rules.
 * @param {Array<{ id?: string, titleKey?: string, icon?: import('react').ComponentType, fields?: Array }>} formSections
 * @returns {{ sections: typeof formSections, showTabs: boolean }}
 */
export function resolveModalFormSections(formSections) {
  if (!formSections?.length) return { sections: [], showTabs: false };

  const fields = formSections.flatMap((section) => section.fields || []);
  const count = fields.length;
  const { NO_TABS_MAX, TWO_TABS_MAX, THREE_TABS_MIN_FIELDS } = ERP_MODAL_TAB_RULES;

  if (count <= NO_TABS_MAX) {
    const first = formSections[0];
    return {
      sections: [{
        id: first.id || 'general',
        titleKey: first.titleKey,
        icon: first.icon,
        fields,
      }],
      showTabs: false,
    };
  }

  if (count <= TWO_TABS_MAX) {
    if (formSections.length === 2) {
      return { sections: formSections, showTabs: true };
    }
    if (formSections.length === 1) {
      const mid = Math.ceil(count / 2);
      return {
        sections: [
          { ...formSections[0], fields: fields.slice(0, mid) },
          {
            id: `${formSections[0].id || 'general'}_details`,
            titleKey: formSections[0].detailsTitleKey || 'section_details',
            icon: formSections[0].icon,
            fields: fields.slice(mid),
          },
        ],
        showTabs: true,
      };
    }
    const splitAt = Math.ceil(formSections.length / 2);
    return {
      sections: [
        {
          id: formSections[0].id,
          titleKey: formSections[0].titleKey,
          icon: formSections[0].icon,
          fields: formSections.slice(0, splitAt).flatMap((s) => s.fields || []),
        },
        {
          id: formSections[splitAt].id,
          titleKey: formSections[splitAt].titleKey,
          icon: formSections[splitAt].icon,
          fields: formSections.slice(splitAt).flatMap((s) => s.fields || []),
        },
      ],
      showTabs: true,
    };
  }

  const targetTabs = count >= THREE_TABS_MIN_FIELDS ? 3 : 2;
  if (formSections.length === targetTabs) {
    return { sections: formSections, showTabs: true };
  }

  const chunkSize = Math.ceil(count / targetTabs);
  return {
    sections: Array.from({ length: targetTabs }, (_, index) => {
      const meta = formSections[Math.min(index, formSections.length - 1)];
      const start = index * chunkSize;
      return {
        id: meta.id || `section_${index}`,
        titleKey: meta.titleKey,
        icon: meta.icon,
        fields: fields.slice(start, start + chunkSize),
      };
    }).filter((section) => section.fields.length > 0),
    showTabs: true,
  };
}

/** @deprecated use ERP_MODAL_FORM_CLASS */
export const ARTICLE_FORM_CLASS = ERP_MODAL_FORM_CLASS;

export function erpFormLabelClass(isLight) {
  return `block w-full text-sm mb-2 font-medium text-right ${
    isLight ? 'text-gray-600' : 'text-white/70'
  }`;
}

/** Split trailing * from locale labels and render required asterisk in ERP red. */
export function ErpFieldLabel({ label, className, as: Tag = 'label' }) {
  const raw = String(label ?? '');
  const required = /\*\s*$/.test(raw.trim());
  const text = raw.replace(/\s*\*\s*$/, '').trim();
  const resolvedClass = className || erpFormLabelClass(true);
  return (
    <Tag className={resolvedClass}>
      {text}
      {required ? <span className="text-red-600 font-bold"> *</span> : null}
    </Tag>
  );
}

/** @deprecated use erpFormLabelClass */
export const articleFormLabelClass = erpFormLabelClass;

const ERP_MODAL_GHOST_LIGHT = {
  green: 'border border-[#6e9b6e]/45 bg-[#e8f1e8] text-[#2e452e] hover:bg-[#6e9b6e]/12 hover:border-[#6e9b6e]/55',
  cyan: 'border border-[#5a85aa]/35 bg-[#ecf4fc] text-[#1e3a5f] hover:bg-[#5a85aa]/10 hover:border-[#5a85aa]/50',
  blue: 'border border-[#5a85aa]/35 bg-[#ecf4fc] text-[#1e3a5f] hover:bg-[#5a85aa]/10 hover:border-[#5a85aa]/50',
  amber: 'border border-[#a67c3d]/40 bg-[#fcf4e8] text-[#5c4520] hover:bg-[#a67c3d]/10 hover:border-[#a67c3d]/55',
};

const ERP_MODAL_GHOST_DARK = {
  green: 'bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30',
  cyan: 'bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 border border-cyan-500/30',
  blue: 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 border border-blue-500/30',
  amber: 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30',
};

/** Ghost action link/button inside view/create modals — readable on Qawaem light glass. */
export function erpModalGhostActionClass(isLight, tone = 'green', extra = '') {
  const palette = isLight ? ERP_MODAL_GHOST_LIGHT : ERP_MODAL_GHOST_DARK;
  const base = palette[tone] || palette.green;
  return `inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${base} ${extra}`.trim();
}

/** Full-width modal footer close button. */
export function erpModalCloseBtnClass(isLight, extra = '') {
  const base = isLight
    ? 'w-full mt-6 bg-[#f4f7f4] hover:bg-[#e8f1e8] text-[#2e452e] py-3 rounded-xl font-semibold transition-all border border-[#6e9b6e]/22 hover:border-[#6e9b6e]/35'
    : 'w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10';
  return `${base} ${extra}`.trim();
}
